import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { authClient, betterAuthErrorText } from "lib/auth/authClient";
import { SKIP_SENTRY_FOR_CLIENT_ERRORS } from "lib/sentryErrors";

/** A device or browser signed in to the user's account. */
export type BetterAuthListedSession = {
  id: string;
  token: string;
  userAgent?: string | null;
  ipAddress?: string | null;
  /** How the session was signed in. Null for sessions created before this was recorded. */
  loginMethod?: string | null;
  createdAt: Date;
  updatedAt: Date;
  /**
   * The session's latest page load, from the auth-service audit log, with the full IP address and
   * country it came from. Null when it has none, e.g. before the audit log recorded page loads.
   */
  lastActiveAt?: Date | null;
  lastActiveIpAddress?: string | null;
  /** ISO 3166-1 alpha-2 country code, from CloudFront. */
  lastActiveCountry?: string | null;
  /** Country code of the request that signed the session in. */
  signInCountry?: string | null;
};

/** A failed sessions request, carrying better-auth's error `code` when the server sent one. */
export class SessionsError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
  }
}

/** better-auth only lists sessions for a sign-in from the last day; older ones get this code. */
export const SESSION_NOT_FRESH = "SESSION_NOT_FRESH";

export const LIST_SESSIONS_QUERY_KEY = ["betterAuthSessions"] as const;

function throwSessionsError(
  error: { status?: number; code?: string; message?: string },
  fallback: string,
): never {
  throw new SessionsError(betterAuthErrorText(error, fallback), error.code, { cause: error });
}

/** The user's sessions, or null when they need to log in again before the list can be shown. */
export function useListSessions() {
  return useQuery<BetterAuthListedSession[] | null, SessionsError>({
    queryKey: LIST_SESSIONS_QUERY_KEY,
    meta: SKIP_SENTRY_FOR_CLIENT_ERRORS,
    queryFn: async () => {
      const { data, error } = await authClient.listSessions();
      // Expected for anyone who signed in over a day ago. Returned rather than thrown so react-query
      // doesn't retry it and the global query error handler doesn't report it to Sentry.
      if (error?.code === SESSION_NOT_FRESH) return null;
      if (error) throwSessionsError(error, "Could not load your sessions");
      return data;
    },
  });
}

/** Signs out one session. better-auth identifies it by its token, not its id. */
export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation<unknown, SessionsError, { token: string }>({
    meta: SKIP_SENTRY_FOR_CLIENT_ERRORS,
    mutationKey: ["revokeSession"],
    mutationFn: async ({ token }) => {
      const { data, error } = await authClient.revokeSession({ token });
      if (error) throwSessionsError(error, "Could not sign out that session");
      return data;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: LIST_SESSIONS_QUERY_KEY }),
  });
}

/** Signs out every session except this one. */
export function useRevokeOtherSessions() {
  const queryClient = useQueryClient();
  return useMutation<unknown, SessionsError>({
    meta: SKIP_SENTRY_FOR_CLIENT_ERRORS,
    mutationKey: ["revokeOtherSessions"],
    mutationFn: async () => {
      const { data, error } = await authClient.revokeOtherSessions();
      if (error) throwSessionsError(error, "Could not sign out your other sessions");
      return data;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: LIST_SESSIONS_QUERY_KEY }),
  });
}
